import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Route, Routes } from "react-router-dom"
import { axiosInstance } from "@/api/service"
import { isTokenValid } from "@/helpers/jwt"
import { getTgWebApp } from "@/helpers/tgWebApp"
import { useAutoAuth } from "@/hooks/useAutoAuth"
import { useCheckPolicy } from "@/hooks/useCheckPolicy"
import { useFindMatchListener } from "@/hooks/useFindMatchListener"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { useTgInsetsEmulation } from "@/hooks/useTgInsetsEmulation"
import { GoldCardsTab } from "@/pages/Shop/ui/GoldCardsTab"
import { TonConnectProvider } from "@/providers/TonConnectProvider"
import { useAppDispatch, useAppSelector } from "@/store"
import { AppSelectors, setIsPhoneDevice } from "@/store/slices/app"
import { DevToolsSelectors } from "@/store/slices/devTools"
import { PlayerSelectors } from "@/store/slices/player"
import { FindMatchLabel } from "@/ui/FindMatchLabel"
import { Loader } from "@/ui/Loader"
import { PageNotFound } from "./404"
import { AppOld } from "./AppOld"
import { Game } from "./Game"
import { Home } from "./Home"
import Login from "./Login"
import { OAuthCallback } from "./OAuthCallback"
import { OAuthErrorRedirect } from "./OAuthErrorRedirect"
import { Quests } from "./Quests"
import { Referrals } from "./Referrals"
import Register from "./Register"
import { Shop } from "./Shop/Shop"
import { SkinsTab } from "./Shop/ui/SkinsTab"
import { TelegramAuthError } from "./TelegramAuthError"
import { Wallet } from "./Wallet"

const RouterAuth = () => {
  return (
    <Routes>
      <Route index element={<Navigate to="/login" replace />} />
      <Route path="login" element={<Login />} />
      <Route path="register" element={<Register />} />
      <Route path="oauth/:provider/login" element={<OAuthCallback />} />
      <Route path="oauth/error" element={<OAuthErrorRedirect />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  )
}

const RouterApp = () => {
  useFindMatchListener()
  useTgInsetsEmulation()
  useCheckPolicy()

  const isLoaded = useAppSelector(AppSelectors.selectIsLoaded())

  if (!isLoaded) {
    return <Loader />
  }

  return (
    <>
      <FindMatchLabel />
      <Routes>
        <Route index element={<Home />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="match/:sessionId" element={<Game />} />
        <Route
          path="shop"
          element={
            <TonConnectProvider>
              <Shop />
            </TonConnectProvider>
          }
        >
          <Route index element={<Navigate to="skins" replace />} />
          <Route path="skins" element={<SkinsTab />} />
          <Route path="gold-card" element={<GoldCardsTab />} />
        </Route>
        <Route
          path="wallet/*"
          element={
            <TonConnectProvider>
              <Wallet />
            </TonConnectProvider>
          }
        />
        <Route path="referrals/*" element={<Referrals />} />
        <Route path="quests/*" element={<Quests />} />
        <Route path="oauth/:provider/login" element={<OAuthCallback />} />
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </>
  )
}

const fetchAppVersion = async (signal: AbortSignal): Promise<string | null> => {
  const { data } = await axiosInstance({
    url: "/version",
    method: "GET",
    signal
  })
  if (import.meta.env.DEV) {
    console.log(
      "APP VERSION: ",
      data?.version,
      import.meta.env.VITE_APP_VERSION,
      data?.version === import.meta.env.VITE_APP_VERSION
        ? "isActualVersion"
        : "isNonActualVersion"
    )
  }
  return data?.version ?? null
}

export const Router: FC = () => {
  const dispatch = useAppDispatch()

  useIsDesktop()
  useAutoAuth()

  const { i18n } = useTranslation()

  const isAuthorized = useAppSelector(PlayerSelectors.selectIsAuthorized())
  const [appVer, setAppVer] = useState<string | null>(null)

  const language = useAppSelector(AppSelectors.selectLanguage())
  const isTMA = useAppSelector(AppSelectors.selectIsTMA())
  const reAuthState = useAppSelector(AppSelectors.selectReAuthState())

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language)
    }
  }, [language, i18n])

  useEffect(() => {
    const controller = new AbortController()

    fetchAppVersion(controller.signal)
      .then((ver) => {
        if (!controller.signal.aborted) setAppVer(ver)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (import.meta.env.DEV) {
          console.warn("checkAppVersion failed", err)
        }
      })

    return () => controller.abort()
  }, [])

  const emulatePhoneDevice = useAppSelector(
    DevToolsSelectors.selectEmulatePhoneDevice()
  )

  useEffect(() => {
    const platform = getTgWebApp()?.platform
    const realIsPhone = platform === "android" || platform === "ios"
    const isPhoneDevice = import.meta.env.DEV ? emulatePhoneDevice : realIsPhone
    dispatch(setIsPhoneDevice(isPhoneDevice))
  }, [dispatch, emulatePhoneDevice])

  if (appVer !== null && import.meta.env.VITE_APP_VERSION !== appVer) {
    return (
      <AppOld currVer={appVer} yourVer={import.meta.env.VITE_APP_VERSION} />
    )
  }

  if (reAuthState === "loading") return null

  if (reAuthState === "error") {
    if (isTMA) return <TelegramAuthError />
    return <RouterAuth />
  }

  return isAuthorized ||
    isTokenValid("accessToken") ||
    isTokenValid("refreshToken") ? (
    <RouterApp />
  ) : (
    <RouterAuth />
  )
}
